import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { clearCurrentUser } from "../hooks/useCurrentUser";
import { IconAlert, IconLoader } from "../components/Icons";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  interface LoginResponse {
    access_token?: string;
    refresh_token?: string;
    requires_email_verification?: boolean;
    message?: string;
  }

  interface ApiError {
    response?: { data?: { detail?: string } };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setVerifyMessage(null);
    setLoading(true);
    try {
      const { data } = await api.post<LoginResponse>("/auth/ops-login", { email, password });
      if (data.requires_email_verification) {
        setVerifyMessage(data.message ?? t("login.verifyRequired"));
        return;
      }
      if (data.access_token && data.refresh_token) {
        localStorage.setItem("lk_ops_access", data.access_token);
        localStorage.setItem("lk_ops_refresh", data.refresh_token);
        clearCurrentUser();
        navigate("/");
      }
    } catch (e: unknown) {
      const ae = e as ApiError;
      setErr(ae.response?.data?.detail ?? t("login.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="card" style={{ width: "100%", maxWidth: 400, padding: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{t("login.title")}</h1>
        <p style={{ color: "var(--text-secondary)", marginBottom: 24 }}>{t("login.subtitle")}</p>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label>{t("login.email")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@firma.cz"
              required
            />
          </div>
          <div>
            <label>{t("login.password")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {err && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: "var(--radius-sm)",
                background: "var(--red-dim)",
                color: "var(--red)",
                fontSize: 13,
              }}
            >
              <IconAlert size={16} />
              {err}
            </div>
          )}

          {verifyMessage && (
            <div
              style={{
                padding: "14px",
                borderRadius: "var(--radius-sm)",
                background: "var(--yellow-dim)",
                color: "var(--yellow)",
                fontSize: 13,
              }}
            >
              {verifyMessage}
            </div>
          )}

          <button type="submit" className="primary" disabled={loading}>
            {loading ? <IconLoader size={18} className="spin" /> : t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
